/**
 * Las invitaciones a un equipo que están esperando respuesta, en el panel.
 *
 * Existe porque el correo no es un canal fiable: en local no hay clave de Resend
 * y no sale ninguno, y en producción está el spam. Sin esto, una invitación vive
 * ENTERA dentro de un enlace que quizá nadie llegue a ver, y quien invitó se
 * queda mirando un asiento ocupado sin saber por qué no contestan.
 *
 * Va arriba del todo y no en una sección: es lo único de la pantalla que le pide
 * algo a quien la mira. Y aquí sí se acepta —a diferencia de la campana, que
 * sólo trae hasta aquí— porque este sitio tiene espacio para decir a qué estás
 * entrando antes de que lo hagas.
 */
import { useCallback, useEffect, useState } from 'react'
import { IconCheck, IconLoader2, IconUserPlus, IconX } from '@tabler/icons-react'
import { PROJECT_ROLES, isProjectRole, sectionsForRole, type ProjectRole } from '@webbuilder/shared-types'
import { acceptMyTeamInvite, fetchMyTeamInvites, rejectMyTeamInvite, type MyTeamInvite } from '../lib/api'
import { notifyInvitesChanged } from '../lib/teamInviteSignal'
import { reportPending } from '../lib/pendingNotices'
import { MascotOutlinePattern } from './MascotOutlinePattern'
import { useI18n, translateServerError } from '../lib/i18n'

export function TeamInviteBanner({ token, onAccepted }: {
  token: string
  /**
   * Se llama al entrar en un proyecto.
   *
   * Con el NOMBRE además del id: quien lo recibe levanta la cortina de cambio de
   * proyecto, y ésa necesita cómo se llama el destino para el relevo. Pedírselo
   * a la lista de proyectos no valdría — el recién aceptado todavía no está en
   * ella, que es medio problema que esto resuelve.
   */
  onAccepted?: (projectId: string, projectName: string, role: ProjectRole) => void
}) {
  const { t } = useI18n()
  const [invites, setInvites] = useState<MyTeamInvite[]>([])
  const [busyId, setBusyId] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    try {
      setInvites(await fetchMyTeamInvites(token))
    } catch {
      /* En silencio: esto es un añadido a la pantalla, no la pantalla. Que falle
         la consulta no puede llenar el panel de alguien con un error sobre algo
         que quizá ni tiene. */
    }
  }, [token])

  useEffect(() => { void load() }, [load])

  /* Que la pantalla sepa que hay algo esperando. La mascota del saludo vive
     debajo y levanta la vista hacia esto; al desmontarse se retira la cuenta,
     porque si no se quedaría mirando a un hueco. Ver `lib/pendingNotices`. */
  useEffect(() => {
    reportPending('invite', invites.length)
    return () => { reportPending('invite', 0) }
  }, [invites.length])

  async function decide(invite: MyTeamInvite, action: 'accept' | 'reject') {
    setBusyId(invite.id)
    setError(null)
    try {
      if (action === 'accept') {
        await acceptMyTeamInvite(invite.id, token)
        onAccepted?.(invite.projectId, invite.projectName, invite.role)
      } else {
        await rejectMyTeamInvite(invite.id, token)
      }
      setInvites((current) => current.filter((row) => row.id !== invite.id))
      /* Y que la campana se entere: enseña la misma invitación desde su propia
         consulta, y sin esto seguía ofreciéndola después de responderla. */
      notifyInvitesChanged()
    } catch (err) {
      setError(translateServerError(err, t('err.actionFailed')))
    } finally {
      setBusyId(null)
    }
  }

  if (invites.length === 0) return null

  return (
    <div className="dash-invite-banners">
      {invites.map((invite) => {
        const busy = busyId === invite.id
        const days = Math.max(
          0,
          Math.ceil((new Date(invite.expiresAt).getTime() - Date.now()) / 86_400_000),
        )
        /* `sectionsForRole` devuelve claves: los nombres de sección los
           comparten cuatro sitios del monorepo y cada uno pinta en su idioma. */
        const sections = (isProjectRole(invite.role) ? sectionsForRole(invite.role) : []).map((key) => t(key))
        return (
          <section className="dash-invite-banner dash-loaded-content-in" key={invite.id}>
            {/* El mismo patrón de la mascota que el banner de la oferta, del
                mismo componente: los dos son la casa dirigiéndose a ti sobre
                fondo sólido, y con dos dibujos distintos dejarían de leerse como
                la misma voz. */}
            {/* Sin ojos: aquí los botones caen justo sobre el centro del patrón,
                y los ojos son las dos únicas formas rellenas del dibujo. Detrás
                del botón de contorno se transparentaban y lo dejaban pareciendo
                roto. En el banner de la oferta sí van, porque allí no hay nada
                encima. */}
            <MascotOutlinePattern className="dash-invite-banner-pattern" eyes={false} />

            <div className="dash-invite-banner-copy">
              <span className="dash-invite-banner-kicker">
                <IconUserPlus size={13} aria-hidden="true" />
                {t('invite.kicker')}
              </span>
              <strong>
                {/* El nombre del proyecto va en cursiva al final, así que la
                    frase se queda fuera y él entra aparte. */}
                {invite.fromName
                  ? <>{t('invite.from', { name: invite.fromName })} <em>{invite.projectName}</em></>
                  : <>{t('invite.fromSomeone')} <em>{invite.projectName}</em></>}
              </strong>
              <p>
                {t('invite.asRole')} <strong>{t(invite.roleLabelKey)}</strong>
                {isProjectRole(invite.role) ? ` · ${t(PROJECT_ROLES[invite.role].summaryKey)}` : ''}
              </p>
              {/* A qué entra, aquí y no tras aceptar: es lo único que hace falta
                  para decidir, y en la campana no cabía. */}
              {sections.length > 0 ? (
                <p className="dash-invite-banner-sections">{sections.join(' · ')}</p>
              ) : null}
              {error ? <p className="dash-invite-banner-error" role="alert">{error}</p> : null}
            </div>

            <div className="dash-invite-banner-actions">
              <span className="dash-invite-banner-expiry">
                {t(days === 1 ? 'invite.expiresOne' : 'invite.expiresMany', { count: days })}
              </span>
              {/* Botones propios y no `dash-secondary-btn` / `dash-primary-btn`:
                  aquéllos están calculados contra el fondo del panel, y el
                  primario ES el acento — sobre un banner de ese mismo color
                  desaparecería. Aquí se invierte: el blanco sólido es la acción
                  principal y el contorno claro la secundaria. */}
              <button
                type="button"
                className="dash-invite-banner-btn"
                disabled={busy}
                onClick={() => void decide(invite, 'reject')}
              >
                <IconX size={14} aria-hidden="true" /> {t('invite.reject')}
              </button>
              <button
                type="button"
                className="dash-invite-banner-btn is-primary"
                disabled={busy}
                onClick={() => void decide(invite, 'accept')}
              >
                {busy ? <IconLoader2 size={14} className="dash-spin" /> : <IconCheck size={14} />}
                {t(busy ? 'invite.entering' : 'invite.accept')}
              </button>
            </div>
          </section>
        )
      })}
    </div>
  )
}
